import { Button, type ButtonProps } from '@avanta/shared/components/ui/Button';
import { Input } from '@avanta/shared/components/ui/Input';
import { DEFAULT_TIMEZONE, LOCIZE_NS } from '@avanta/shared/constants/common';
import { NICKNAME_MAX_LENGTH } from '@avanta/shared/constants/validations';
import { useFetchCentersByEnterprise } from '@avanta/shared/hooks/useFetchCentersByEnterprise';
import { useFetchDepartments } from '@avanta/shared/hooks/useFetchDepartments';
import { type Employee } from '@avanta/shared/types/employees';
import { cn } from '@avanta/shared/utils/cn';
import { getBirthDateParts } from '@avanta/shared/utils/date';
import { useForm, useWatch, type RegisterOptions } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useCreateEmployee } from 'hooks/useCreateEmployee';
import { useEnterpriseId } from 'stores/user';
import { type CreateEmployeePayload } from 'types/employees';
import { generateSecurePassword } from 'utils/common';
import {
  ADD_EMPLOYEE_FORM_DEFAULT_VALUES,
  type AddEmployeeFormValues,
  addEmployeeForm,
} from './model';

type AddEmployeeFormMode = 'standalone' | 'embedded';

interface AddEmployeeFormProps {
  mode: AddEmployeeFormMode;
  buttons?: {
    cancel?: ButtonProps;
    submit?: ButtonProps;
  };
  onSuccess: (employee: Employee) => void;
}

export const AddEmployeeForm = ({
  mode,
  buttons,
  onSuccess,
}: AddEmployeeFormProps): React.ReactElement => {
  const enterpriseId = useEnterpriseId();
  const { mutate: createEmployee, isPending: isCreatingEmployee } = useCreateEmployee();
  const { t } = useTranslation(LOCIZE_NS.INSURANCE);
  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isDirty, isValid },
  } = useForm<AddEmployeeFormValues>({
    defaultValues: ADD_EMPLOYEE_FORM_DEFAULT_VALUES,
    mode: 'onChange',
  });
  const centerId = useWatch({ control, name: 'centerId', defaultValue: '' });

  const { data: centers } = useFetchCentersByEnterprise({
    enterpriseId,
    isProfileSetup: false,
  });

  const { data: departments, isLoading: isLoadingDepartments } = useFetchDepartments({
    centerId,
    isProfileSetup: false,
  });
  const { onClick: cancelOnClick, ...cancelButton } = buttons?.cancel ?? {};
  const { onClick: submitOnClick, ...submitButton } = buttons?.submit ?? {};

  const handleSubmitButtonClick = (event: React.MouseEvent<HTMLButtonElement>): void => {
    void submitForm(event);
    submitOnClick?.(event);
  };

  const handleOnSubmit = (values: AddEmployeeFormValues): void => {
    const { email, firstName, surname, nif, contactPhone, gender, dateOfBirth } = values;
    const employee: CreateEmployeePayload = {
      email,
      password: generateSecurePassword(),
      nickname: email.split('@')[0].slice(0, NICKNAME_MAX_LENGTH),
      firstName,
      surname,
      nif,
      contactPhone,
      gender: Number(gender),
      timeZone: DEFAULT_TIMEZONE,
      enterpriseId,
      ...getBirthDateParts(new Date(dateOfBirth)),
      centerId: values.centerId,
      departmentId: departments?.[0]?.value || '',
    };

    createEmployee(employee, {
      onSuccess: response => onSuccess(response.data),
    });
  };

  const submitForm = handleSubmit(handleOnSubmit);
  const isStandalone = mode === 'standalone';
  const optionsMap = {
    centerId: centers,
  };

  const content = (
    <>
      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        {Object.entries(addEmployeeForm).map(([id, input]) => {
          const { component: Component } = input;
          const formId = id as keyof AddEmployeeFormValues;
          const formError = errors[formId];
          const options = optionsMap[formId as keyof typeof optionsMap] ?? input.options ?? [];
          const isControllerComponent = Component !== Input;
          const rules: RegisterOptions<AddEmployeeFormValues, typeof formId> = {
            required: input.required,
            ...(input.pattern && { pattern: input.pattern }),
            ...(input.validate && { validate: input.validate }),
            ...(input.minLength && { minLength: input.minLength }),
            ...(input.maxLength && { maxLength: input.maxLength }),
          };
          const helperText = formError?.message ? t(formError.message) : undefined;

          return (
            <div key={id}>
              {isControllerComponent ? (
                <Component
                  control={control}
                  name={formId}
                  className="w-full"
                  label={t(input.label)}
                  placeholder={t(input.placeholder ?? '')}
                  options={options}
                  rules={rules}
                  variant={formError ? 'invalid' : 'filled'}
                  helperText={helperText}
                  leftIcon={input.leftIcon}
                />
              ) : (
                <Input
                  type={input.type}
                  className="w-full"
                  label={t(input.label)}
                  placeholder={t(input.placeholder ?? '')}
                  {...register(formId, rules)}
                  variant={formError ? 'invalid' : 'filled'}
                  helperText={helperText}
                  leftIcon={input.leftIcon}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className={cn('flex gap-2', isStandalone ? 'justify-end' : 'justify-between')}>
        <Button
          {...cancelButton}
          variant={cancelButton.variant ?? 'destructive_terciary'}
          type="button"
          className={cn('w-fit', cancelButton.className)}
          onClick={cancelOnClick}
          size={cancelButton.size ?? 'lg'}
          icon={cancelButton.icon ?? 'leading'}
        >
          {cancelButton.children ?? t('Common.cancel', { ns: LOCIZE_NS.COMMON })}
        </Button>
        <Button
          {...submitButton}
          type={isStandalone ? 'submit' : 'button'}
          variant={submitButton.variant ?? 'secondary'}
          className={cn('min-w-40', submitButton.className)}
          disabled={!isDirty || !isValid || isLoadingDepartments}
          isLoading={isCreatingEmployee}
          size={submitButton.size ?? 'lg'}
          onClick={isStandalone ? submitOnClick : handleSubmitButtonClick}
        >
          {submitButton.children ?? t('InsuredPersonsList.addPerson.cta')}
        </Button>
      </div>
    </>
  );

  if (isStandalone) {
    return (
      <form
        aria-label={t('InsuredPersonsList.addPerson.cta')}
        className="flex flex-col gap-8"
        onSubmit={submitForm}
      >
        {content}
      </form>
    );
  }

  return <div className="flex w-3xl flex-col gap-10">{content}</div>;
};
